import { action, computed, observable } from 'mobx'
import { PropertyType, type TreatPropertyAs } from '@taxfyle/txf-api-client'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { validationContext, required } from 'validx'
import {
  mustBeInRange,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  validateObject,
  when,
} from '@/utils/validx-util'
import { AddressFieldSetViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/AddressFieldSet'
import {
  mapOwnerToTaxActivityAssignee,
  type Owner,
} from '../../../TaxPrepOrganizerViewModel'
import {
  type Form1099PaymentsInfo,
  type PropertyInformation,
  type RentalActivity,
  type RentalActivityProps,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { RENTAL_PROPERTY_TYPES_MAP } from '../../../data/rental-property-types'

export class RentalAccordionViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  @observable accessor isExpanded = false

  @observable accessor propertyName = ''
  @observable accessor treatAs: TreatPropertyAs | null = null
  @observable accessor propertyType: PropertyType | null = null
  @observable accessor propertyTypeDescription = ''
  @observable accessor ownerType: Owner['type'] | null = null
  @observable accessor propertyPlacedInServiceDuringYear: boolean | null = null
  @observable accessor propertyWasDisposedOfDuringYear: boolean | null = null
  @observable accessor carryToForm8960: boolean | null = null
  @observable accessor qualifiedJointVenture: boolean | null = null
  @observable accessor electingOutOfBusinessInterestExpenseLimit:
    | boolean
    | null = null
  @observable accessor isTradeOrBusiness: boolean | null = null
  @observable accessor didMakePaymentsRequiring1099: boolean | null = null
  @observable accessor willFileForm1099: boolean | null = null

  @observable accessor percentageOfOwnership = ''
  @observable accessor fairRentalDays = ''
  @observable accessor personalUseDays = ''
  @observable accessor percentageOfTenantOccupancy = ''
  @observable accessor mainHomeOrSecondHome: boolean | null = null

  readonly address = new AddressFieldSetViewModel(true)

  readonly validation = validationContext(this, {
    ownerType: [required('Owner is required')],
    propertyName: [
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(20, 'Property name must not exceed 20 characters'),
    ],
    propertyType: [required('Property type is required')],
    propertyTypeDescription: [
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(
        20,
        'Property type description must not exceed 20 characters',
      ),
    ],
    percentageOfOwnership: [
      mustBeInRange(
        0,
        100,
        'Percentage of ownership must be between 0 and 100',
      ),
    ],
    fairRentalDays: [
      mustBeInRange(0, 366, 'Fair rental days must be between 0 and 366'),
    ],
    personalUseDays: [
      mustBeInRange(0, 366, 'Personal use days must be between 0 and 366'),
    ],
    percentageOfTenantOccupancy: [
      mustBeInRange(
        0,
        100,
        'Percentage of tenant occupancy must be between 0 and 100',
      ),
    ],
    address: [validateObject()],
    willFileForm1099: [
      when(
        () => this.didMakePaymentsRequiring1099 === true,
        required(
          'Will you file the required 1099 forms for those payments? is required',
        ),
      ),
    ],
  })

  constructor(
    private readonly _model: RentalActivity,
    private readonly _index: number,
    private readonly _year: number,
    private readonly _getOwners: () => Owner[],
  ) {
    this.syncFromModel()
  }

  @computed
  get index() {
    return this._index
  }

  @computed
  get year() {
    return this._year
  }

  @computed
  get id() {
    return this._model.id
  }

  @computed
  get isArchived() {
    return this._model.archived
  }

  @computed
  get displayName() {
    return (
      this.address.streetLine ||
      this.propertyName ||
      `Rental Property ${this._index + 1}`
    )
  }

  @computed
  get displaySubtitle() {
    return this.propertyType
      ? RENTAL_PROPERTY_TYPES_MAP[this.propertyType]
      : null
  }

  @computed
  get owners() {
    return this._getOwners()
  }

  @computed
  get owner(): Owner {
    return (
      this.owners.find((owner) => owner.type === this.ownerType) ?? {
        type: 'UNASSIGNED',
        label: 'Unassigned',
      }
    )
  }

  toProps(): RentalActivityProps {
    const propertyInformation: PropertyInformation | null = this.propertyType
      ? this.propertyType === PropertyType.Other
        ? {
            type: PropertyType.Other,
            description: this.propertyTypeDescription || null,
          }
        : { type: this.propertyType }
      : null

    const form1099Payments: Form1099PaymentsInfo | null =
      this.didMakePaymentsRequiring1099 !== null
        ? this.didMakePaymentsRequiring1099
          ? {
              didMakePayments: true,
              willFileForm1099: this.willFileForm1099 ?? false,
            }
          : { didMakePayments: false }
        : null

    return {
      id: this._model.id,
      assignedTo: mapOwnerToTaxActivityAssignee(this.owner),
      propertyDescription: this.propertyName || null,
      treatAs: this.treatAs,
      propertyInformation,
      address: this.address.toProps(),
      percentageOfOwnership: this.percentageOfOwnership
        ? Number(this.percentageOfOwnership)
        : null,
      fairRentalDays: this.fairRentalDays ? Number(this.fairRentalDays) : null,
      personalUseDays: this.personalUseDays
        ? Number(this.personalUseDays)
        : null,
      percentageOfTenantOccupancy: this.percentageOfTenantOccupancy
        ? Number(this.percentageOfTenantOccupancy)
        : null,
      propertyPlacedInServiceDuringYear: this.propertyPlacedInServiceDuringYear,
      propertyWasDisposedOfDuringYear: this.propertyWasDisposedOfDuringYear,
      carryToForm8960: this.carryToForm8960,
      mainHomeOrSecondHome: this.mainHomeOrSecondHome,
      qualifiedJointVenture: this.qualifiedJointVenture,
      electingOutOfBusinessInterestExpenseLimit:
        this.electingOutOfBusinessInterestExpenseLimit,
      isTradeOrBusiness: this.isTradeOrBusiness,
      form1099Payments,
      archived: this._model.archived,
    }
  }

  @action.bound
  toggleExpanded() {
    this.isExpanded = !this.isExpanded
  }

  @action.bound
  expand() {
    this.isExpanded = true
  }

  @action.bound
  syncFromModel() {
    const model = this._model
    this.ownerType = model.assignedTo?.type ?? null
    this.propertyName = model.propertyDescription ?? ''
    this.treatAs = model.treatAs
    this.percentageOfOwnership = model.percentageOfOwnership
      ? String(model.percentageOfOwnership)
      : ''
    this.propertyType = model.propertyInformation?.type ?? null
    if (model.propertyInformation?.type === PropertyType.Other) {
      this.propertyTypeDescription = model.propertyInformation.description ?? ''
    }
    this.address.reset(model.address)
    this.fairRentalDays = model.fairRentalDays
      ? String(model.fairRentalDays)
      : ''
    this.personalUseDays = model.personalUseDays
      ? String(model.personalUseDays)
      : ''
    this.percentageOfTenantOccupancy = model.percentageOfTenantOccupancy
      ? String(model.percentageOfTenantOccupancy)
      : ''
    this.propertyPlacedInServiceDuringYear =
      model.propertyPlacedInServiceDuringYear
    this.propertyWasDisposedOfDuringYear = model.propertyWasDisposedOfDuringYear
    this.carryToForm8960 = model.carryToForm8960
    this.mainHomeOrSecondHome = model.mainHomeOrSecondHome
    this.qualifiedJointVenture = model.qualifiedJointVenture
    this.electingOutOfBusinessInterestExpenseLimit =
      model.electingOutOfBusinessInterestExpenseLimit
    this.isTradeOrBusiness = model.isTradeOrBusiness

    if (model.form1099Payments) {
      this.didMakePaymentsRequiring1099 = model.form1099Payments.didMakePayments
      this.willFileForm1099 = model.form1099Payments.didMakePayments
        ? model.form1099Payments.willFileForm1099
        : null
    } else {
      this.didMakePaymentsRequiring1099 = null
      this.willFileForm1099 = null
    }

    this.validation.reset()
  }

  activate() {
    this.lifecycle.activate()
  }

  deactivate() {
    this.lifecycle.deactivate()
  }
}
